import { CalendarIcon } from "@images/box/calendarIcon"
import { BoxSlotTemplateFormikType, BoxSlotTemplateInputKey } from "@modules/boxSlotTemplate/boxSlotTemplateFormikType"
import { word } from "@modules/core/utils/i18n"
import { HUButton } from "@modules/ui/components/huButton"
import { HUText } from "@modules/ui/components/huText"
import { HUDialog } from "@modules/ui/components/popup/huDialog"
import { formatDateWithHyphen } from "@modules/utils/dateUtils"
import { SimpleFormik } from "@src/typings/formik"
import dayjs from "dayjs"
import { useCallback, useImperativeHandle, useState } from "react"
import { useTheme } from "styled-components"

export type BoxSlotTemplateFormModalRef = {
  open: () => void
  changedKeys: () => BoxSlotTemplateInputKey[]
}

export type BoxSlotTemplateFormModalProps = {
  formikInitialValues: BoxSlotTemplateFormikType
  formik: SimpleFormik<BoxSlotTemplateFormikType, BoxSlotTemplateInputKey>
  handleSubmit: () => Promise<void>
  ref: React.Ref<BoxSlotTemplateFormModalRef>
}

type tagProps = {
  title: string
  value: BoxSlotTemplateInputKey[]
  notification: boolean
}[]

export function BoxSlotTemplateFormModal({
  formik,
  formikInitialValues,
  handleSubmit,
  ref,
}: BoxSlotTemplateFormModalProps) {
  const [showModal, setShowModal] = useState(false)
  const [isSubmit, setIsSubmit] = useState(false)
  const theme = useTheme()
  const [isSelected, setIsSelected] = useState({
    now: false,
    future: false,
  })

  const tag: tagProps = [
    {
      title: word("box.slotTemplates.form.modal.tag.scheduleChange"),
      value: ["day", "startDateTime", "endDateTime"],
      notification: true,
    },
    {
      title: word("box.slotTemplates.form.modal.tag.reservation"),
      value: ["limitEnd", "limitStart", "allowMultiBooking"],
      notification: false,
    },
    {
      title: word("box.slotTemplates.form.modal.tag.hostingCapacity"),
      value: ["participantLimit"],
      notification: false,
    },
    {
      title: word("box.slotTemplates.form.modal.tag.areaChange"),
      value: ["location"],
      notification: true,
    },
  ]

  const changedKeys = (): BoxSlotTemplateInputKey[] => {
    const changedKeys: BoxSlotTemplateInputKey[] = []
    for (const key in formikInitialValues) {
      if (
        formikInitialValues[key as keyof BoxSlotTemplateFormikType]?.toString() !==
        formik.values[key as BoxSlotTemplateInputKey]?.toString()
      ) {
        changedKeys.push(key as BoxSlotTemplateInputKey)
      }
    }
    return changedKeys
  }

  const onHide = useCallback(() => {
    setShowModal(false)
    setIsSubmit(false)
  }, [])

  const onSubmit = async () => {
    setIsSubmit(true)
    await formik.setFieldValue("impactAlreadyCreatedSlot", isSelected.now)
    await handleSubmit()
    onHide()
  }

  useImperativeHandle(ref, () => ({
    open: () => {
      setShowModal(true)
    },
    changedKeys: () => changedKeys(),
  }))

  const dateForTwoWeek = new Date()
  dateForTwoWeek.setDate(dateForTwoWeek.getDate() + 15)

  return (
    <HUDialog visible={showModal} header={null} onHide={onHide} style={{ width: "512px" }}>
      {/* HEADER */}
      <div className="flex flex-column flex-wrap pt-5 gap-2 justify-content-center align-content-center text-center">
        <div className="flex w-full justify-content-center align-content-center">
          <CalendarIcon color={theme.mainWhite} />
        </div>
        <HUText fontStyle="TL" color={theme.mainWhite}>
          {word("box.slotTemplates.form.modal.header.title")}
        </HUText>
        <div className="flex flex-wrap gap-2 justify-content-center align-content-center">
          {tag
            .filter((t) => t.value.some((v) => changedKeys().includes(v)))
            .map((t, index) => (
              <HUText
                key={index}
                fontStyle="LL"
                color={theme.mainWhite}
                className="py-2 px-3 rounded-full bg-neutralColor-100"
              >
                {t.title}
              </HUText>
            ))}
        </div>
        <HUText fontStyle="BS" color={theme.neutralColor600}>
          {word("box.slotTemplates.form.modal.header.subtitle")}
        </HUText>
      </div>
      {/* CONTENT */}
      <HUText fontStyle="BM" className="flex mt-5 text-center" color={theme.neutralColor600}>
        {word("box.slotTemplates.form.modal.content.title")}
      </HUText>
      <div className="flex gap-3 mt-3">
        <div
          className={`flex flex-column w-6 p-3 gap-3 cursor-pointer border-1 border-round-2xl ${isSelected.now ? "border-electricPurple-400" : "cursor-pointer border-neutralColor-700 "}`}
          onClick={() => setIsSelected({ now: true, future: false })}
        >
          <HUText fontStyle="TS" className="text-center" color={theme.mainWhite}>
            {word("box.slotTemplates.form.modal.content.now")}
          </HUText>
          {tag.some((t) => t.notification && t.value.some((v) => changedKeys().includes(v))) && (
            <HUText fontStyle="BS" className="line-height-3 text-center" color={theme.neutralColor300}>
              {word("box.slotTemplates.form.modal.content.athele")}
              <br />
              {word("box.slotTemplates.form.modal.content.notification")}
              <br />
              {word("box.slotTemplates.form.modal.content.email")}
            </HUText>
          )}
        </div>
        <div
          className={`flex flex-column w-6 p-3 gap-3 cursor-pointer border-1 border-round-2xl ${isSelected.future ? "border-electricPurple-400" : "cursor-pointer border-neutralColor-700"}`}
          onClick={() => setIsSelected({ now: false, future: true })}
        >
          <HUText fontStyle="TS" className="  text-center" color={theme.mainWhite}>
            {word("box.slotTemplates.form.modal.content.future")}
          </HUText>
          <HUText fontStyle="BS" className=" text-center" color={theme.neutralColor300}>
            {` ${word("box.slotTemplates.form.modal.content.starting")} ${formatDateWithHyphen(dayjs(dateForTwoWeek).format("L"))}`}
          </HUText>
        </div>
      </div>
      {/* BUTTON */}
      <div className=" mt-5 flex flex-row gap-2 align-items-center justify-content-center">
        <HUButton
          type="Left_Icon"
          size="M"
          colorType="Quaternary"
          text={word("global.cancel")}
          onClick={() => onHide()}
          className="w-full"
        />
        <HUButton
          type="Right_Icon"
          size="M"
          colorType="Primary"
          text={word("global.check")}
          onClick={async () => await onSubmit()}
          disabled={(!isSelected.future && !isSelected.now) || isSubmit}
          className="w-full"
        />
      </div>
    </HUDialog>
  )
}
